'use client';
import React, { useEffect } from 'react';
import Navigation from '../homepage/components/Navigation';
import Footer from '../homepage/components/Footer';
import WhatsAppFloatingButton from '../homepage/components/WhatsAppFloatingButton';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface TeamMember {
  id: string;
  name: string;
  role: string;
  bio: string;
  image: string;
  alt: string;
}

interface Certification {
  id: string;
  name: string;
  issuer: string;
  year: string;
  description: string;
  iconName: string;
  link: string;
}

const AboutPage: React.FC = () => {
  useEffect(() => {
    // Scroll reveal animation
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
        }
      });
    }, observerOptions);

    const revealElements = document.querySelectorAll('.reveal');
    revealElements.forEach((el) => observer.observe(el));

    return () => {
      revealElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  const teamMembers: TeamMember[] = [
  {
    id: 'founder',
    name: 'Jean-Pierre Mulumbu',
    role: 'Founder & CEO',
    bio: 'With over 20 years of experience in the gold mining industry, Jean-Pierre founded Mulumbu Minerals to bring transparency and ethical practices to gold export from the DRC.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_192a5ee06-1763296776968.png",
    alt: 'Jean-Pierre Mulumbu, Founder and CEO of Mulumbu Minerals, standing in front of gold mining facility in Butembo'
  },
  {
    id: 'operations',
    name: 'Marie Kasongo',
    role: 'Director of Operations',
    bio: 'Marie oversees all mining operations and ensures compliance with international standards. Her expertise in logistics has streamlined our export processes.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_12a5d3226-1771584338087.png",
    alt: 'Marie Kasongo, Director of Operations, reviewing gold quality control processes at Mulumbu Minerals facility'
  },
  {
    id: 'quality',
    name: 'Dr. Emmanuel Kabila',
    role: 'Chief Quality Officer',
    bio: 'Dr. Kabila holds a PhD in Metallurgy and leads our quality assurance team, ensuring every shipment meets the highest international standards.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_153f7947c-1770602855078.png",
    alt: 'Dr. Emmanuel Kabila, Chief Quality Officer, conducting gold purity testing in laboratory'
  },
  {
    id: 'compliance',
    name: 'Sarah Mwamba',
    role: 'Compliance Manager',
    bio: 'Sarah ensures all our operations adhere to Kimberley Process standards and international regulations, maintaining our certifications and ethical practices.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fac6e4e1-1764647839218.png",
    alt: 'Sarah Mwamba, Compliance Manager, reviewing certification documents at Mulumbu Minerals office'
  }];


  const certifications: Certification[] = [
  {
    id: 'kimberley',
    name: 'Kimberley Process Certification',
    issuer: 'Kimberley Process Certification Scheme',
    year: '2019',
    description: 'International certification ensuring conflict-free gold sourcing and ethical mining practices. This certification guarantees that our gold is sourced responsibly and does not fund conflict.',
    iconName: 'CheckBadgeIcon',
    link: 'https://www.kimberleyprocess.com'
  },
  {
    id: 'lbma',
    name: 'LBMA Good Delivery',
    issuer: 'London Bullion Market Association',
    year: '2020',
    description: 'Accredited refiner status on the LBMA Good Delivery List, the global standard for gold quality and trading. This ensures our gold meets the highest purity and quality standards.',
    iconName: 'ShieldCheckIcon',
    link: 'https://www.lbma.org.uk'
  },
  {
    id: 'iso',
    name: 'ISO 9001:2015',
    issuer: 'International Organization for Standardization',
    year: '2021',
    description: 'Quality Management System certification demonstrating our commitment to consistent quality, customer satisfaction, and continuous improvement in all operations.',
    iconName: 'StarIcon',
    link: 'https://www.iso.org'
  },
  {
    id: 'rjc',
    name: 'RJC Code of Practices',
    issuer: 'Responsible Jewellery Council',
    year: '2022',
    description: 'Certification for responsible business practices covering human rights, labor rights, environmental impact, and product disclosure throughout the gold supply chain.',
    iconName: 'GlobeAltIcon',
    link: 'https://www.responsiblejewellery.com'
  }];


  return (
    <main className="min-h-screen bg-background">
      <Navigation />

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center px-6 md:px-12 pt-40 pb-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <AppImage
            src="https://img.rocket.new/generatedImages/rocket_gen_img_1eee10751-1768560521489.png"
            alt="Aerial view of Mulumbu Minerals gold mining operations in Butembo, North Kivu, Democratic Republic of Congo"
            className="w-full h-full object-cover"
            priority />
          
          <div className="absolute inset-0 bg-gradient-to-b from-accent/80 via-accent/60 to-background"></div>
        </div>

        <div className="relative z-10 text-center max-w-4xl mx-auto">
          <span className="text-xs font-body font-bold uppercase tracking-[0.4em] text-primary mb-4 block animate-fade-in-up">
            Our Story
          </span>
          <h1 className="text-6xl md:text-7xl font-display font-bold tracking-tight text-white mb-6 animate-fade-in-up [animation-delay:0.2s]">
            About Mulumbu Minerals
          </h1>
          <p className="text-xl text-white/90 font-body leading-relaxed animate-fade-in-up [animation-delay:0.4s]">
            Pioneering ethical gold export from the heart of Congo since 2018
          </p>
        </div>
      </section>

      {/* Company Story Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-card">
        <div className="w-full max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div className="reveal">
              <span className="text-xs font-body font-bold uppercase tracking-[0.4em] text-muted-foreground mb-4 block">
                Our Journey
              </span>
              <h2 className="text-5xl md:text-6xl font-display font-bold tracking-tight text-foreground mb-6">
                From Local Roots to Global Excellence
              </h2>
              <div className="space-y-4 text-lg text-muted-foreground font-body leading-relaxed">
                <p>
                  Founded in 2018 in Butembo, North Kivu, Democratic Republic of Congo, Mulumbu Minerals was born from a vision to transform the gold mining industry in Central Africa.
                </p>
                <p>
                  Our founder, Jean-Pierre Mulumbu, witnessed firsthand the challenges facing local miners and the lack of transparency in the gold export market. He set out to create a company that would bridge the gap between artisanal miners and international buyers while maintaining the highest ethical standards.
                </p>
                <p>
                  Today, we stand as a beacon of responsible mining practices, providing fair prices to local miners and delivering certified, conflict-free gold to clients worldwide.
                </p>
              </div>
            </div>

            <div className="reveal [animation-delay:0.2s]">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl hover-lift transition-smooth">
                <AppImage
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_15acbcb73-1771584339979.png"
                  alt="Mulumbu Minerals facility in Butembo showing modern gold processing equipment and local workers"
                  className="w-full h-full object-cover" />
                
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founding History Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-accent text-white">
        <div className="w-full max-w-6xl mx-auto">
          <div className="text-center mb-16 reveal">
            <span className="text-xs font-body font-bold uppercase tracking-[0.4em] text-primary mb-4 block">
              Our Heritage
            </span>
            <h2 className="text-5xl md:text-6xl font-display font-bold tracking-tight mb-6">
              Rooted in Butembo, North Kivu
            </h2>
            <p className="text-xl text-white/80 font-body max-w-3xl mx-auto leading-relaxed">
              The Democratic Republic of Congo is home to some of the world's richest gold deposits. Our location in Butembo positions us at the heart of this resource-rich region.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="glass-card-dark p-8 rounded-3xl border border-white/10 hover-lift transition-smooth reveal">
              <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center mb-6">
                <Icon name="MapPinIcon" size={32} className="text-primary" />
              </div>
              <h3 className="text-2xl font-display font-bold mb-4">Strategic Location</h3>
              <p className="text-white/70 font-body leading-relaxed">
                Butembo's proximity to major mining sites allows us to work directly with local miners, ensuring fair compensation and ethical sourcing.
              </p>
            </div>

            <div className="glass-card-dark p-8 rounded-3xl border border-white/10 hover-lift transition-smooth reveal [animation-delay:0.1s]">
              <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center mb-6">
                <Icon name="UsersIcon" size={32} className="text-primary" />
              </div>
              <h3 className="text-2xl font-display font-bold mb-4">Community Impact</h3>
              <p className="text-white/70 font-body leading-relaxed">
                We've created over 200 jobs in the region and support local communities through education and infrastructure development programs.
              </p>
            </div>

            <div className="glass-card-dark p-8 rounded-3xl border border-white/10 hover-lift transition-smooth reveal [animation-delay:0.2s]">
              <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center mb-6">
                <Icon name="SparklesIcon" size={32} className="text-primary" />
              </div>
              <h3 className="text-2xl font-display font-bold mb-4">Quality Heritage</h3>
              <p className="text-white/70 font-body leading-relaxed">
                The gold from North Kivu is renowned for its exceptional purity, with many deposits yielding 22K to 24K gold naturally.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-card">
        <div className="w-full max-w-6xl mx-auto">
          <div className="text-center mb-16 reveal">
            <span className="text-xs font-body font-bold uppercase tracking-[0.4em] text-muted-foreground mb-4 block">
              What Drives Us
            </span>
            <h2 className="text-5xl md:text-6xl font-display font-bold tracking-tight text-foreground mb-6">
              Mission & Values
            </h2>
          </div>

          <div className="glass-card p-12 rounded-3xl border border-border shadow-xl mb-12 reveal">
            <div className="flex items-start gap-6 mb-8">
              <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name="LightBulbIcon" size={40} className="text-primary" />
              </div>
              <div>
                <h3 className="text-3xl font-display font-bold text-foreground mb-4">Our Mission</h3>
                <p className="text-xl text-muted-foreground font-body leading-relaxed">
                  To revolutionize the gold export industry by establishing a transparent, ethical, and sustainable supply chain that benefits local communities while delivering premium quality gold to international markets. We are committed to proving that profitability and social responsibility can coexist.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="glass-card p-8 rounded-3xl border border-border hover-lift transition-smooth reveal">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Icon name="ShieldCheckIcon" size={24} className="text-primary" />
                </div>
                <h3 className="text-2xl font-display font-bold text-foreground">Integrity</h3>
              </div>
              <p className="text-muted-foreground font-body leading-relaxed">
                We operate with complete transparency, ensuring every transaction is documented and every certification is earned through rigorous compliance.
              </p>
            </div>

            <div className="glass-card p-8 rounded-3xl border border-border hover-lift transition-smooth reveal [animation-delay:0.1s]">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Icon name="HeartIcon" size={24} className="text-primary" />
                </div>
                <h3 className="text-2xl font-display font-bold text-foreground">Community First</h3>
              </div>
              <p className="text-muted-foreground font-body leading-relaxed">
                Our success is measured not just in profits, but in the positive impact we create for miners, their families, and the broader community.
              </p>
            </div>

            <div className="glass-card p-8 rounded-3xl border border-border hover-lift transition-smooth reveal [animation-delay:0.2s]">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Icon name="StarIcon" size={24} className="text-primary" />
                </div>
                <h3 className="text-2xl font-display font-bold text-foreground">Excellence</h3>
              </div>
              <p className="text-muted-foreground font-body leading-relaxed">
                We maintain the highest quality standards in every aspect of our operations, from sourcing to processing to delivery.
              </p>
            </div>

            <div className="glass-card p-8 rounded-3xl border border-border hover-lift transition-smooth reveal [animation-delay:0.3s]">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Icon name="GlobeAltIcon" size={24} className="text-primary" />
                </div>
                <h3 className="text-2xl font-display font-bold text-foreground">Sustainability</h3>
              </div>
              <p className="text-muted-foreground font-body leading-relaxed">
                We implement environmentally responsible mining practices and invest in long-term sustainability initiatives for future generations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-background">
        <div className="w-full max-w-7xl mx-auto">
          <div className="text-center mb-16 reveal">
            <span className="text-xs font-body font-bold uppercase tracking-[0.4em] text-muted-foreground mb-4 block">
              Meet Our Team
            </span>
            <h2 className="text-5xl md:text-6xl font-display font-bold tracking-tight text-foreground mb-6">
              Leadership That Delivers
            </h2>
            <p className="text-xl text-muted-foreground font-body max-w-3xl mx-auto leading-relaxed">
              Our experienced team brings together expertise in mining, quality assurance, compliance, and international trade.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers.map((member, index) =>
            <div
              key={member.id}
              className="group glass-card rounded-3xl overflow-hidden border border-border shadow-lg hover:shadow-2xl transition-smooth hover:-translate-y-3 reveal"
              style={{ animationDelay: `${index * 0.1}s` }}>
              
                <div className="relative h-80 overflow-hidden">
                  <AppImage
                  src={member.image}
                  alt={member.alt}
                  className="w-full h-full object-cover group-hover:scale-110 transition-smooth" />
                
                  <div className="absolute inset-0 bg-gradient-to-t from-accent via-accent/50 to-transparent opacity-60"></div>
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-display font-bold text-foreground mb-2 group-hover:text-primary transition-smooth">
                    {member.name}
                  </h3>
                  <p className="text-sm font-body font-semibold text-primary mb-4 uppercase tracking-wider">
                    {member.role}
                  </p>
                  <p className="text-sm text-muted-foreground font-body leading-relaxed">
                    {member.bio}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-card">
        <div className="w-full max-w-7xl mx-auto">
          <div className="text-center mb-16 reveal">
            <span className="text-xs font-body font-bold uppercase tracking-[0.4em] text-muted-foreground mb-4 block">
              Trust & Compliance
            </span>
            <h2 className="text-5xl md:text-6xl font-display font-bold tracking-tight text-foreground mb-6">
              Our Certifications
            </h2>
            <p className="text-xl text-muted-foreground font-body max-w-3xl mx-auto leading-relaxed">
              We maintain the highest international standards through rigorous certification and continuous compliance monitoring.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {certifications.map((cert, index) =>
            <div
              key={cert.id}
              className="group glass-card p-8 rounded-3xl border border-border shadow-lg hover:shadow-2xl transition-smooth hover:-translate-y-2 reveal"
              style={{ animationDelay: `${index * 0.1}s` }}>
              
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary transition-smooth group-hover:scale-110">
                    <Icon
                    name={cert.iconName as any}
                    size={32}
                    className="text-primary group-hover:text-primary-foreground transition-smooth" />
                  
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-display font-bold text-foreground mb-2 group-hover:text-primary transition-smooth">
                      {cert.name}
                    </h3>
                    <p className="text-sm font-body text-muted-foreground mb-1">
                      <span className="font-semibold">Issuer:</span> {cert.issuer}
                    </p>
                    <p className="text-sm font-body text-muted-foreground mb-4">
                      <span className="font-semibold">Certified Since:</span> {cert.year}
                    </p>
                    <p className="text-base text-muted-foreground font-body leading-relaxed mb-4">
                      {cert.description}
                    </p>
                    <a
                    href={cert.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-body font-semibold text-primary hover:text-primary/80 transition-smooth group/link">
                    
                      Learn More
                      <Icon
                      name="ArrowTopRightOnSquareIcon"
                      size={16}
                      className="group-hover/link:translate-x-1 group-hover/link:-translate-y-1 transition-transform" />
                    
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Certification Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16 reveal">
            <div className="glass-card p-8 rounded-2xl border border-border text-center hover-lift transition-smooth">
              <div className="text-5xl font-display font-bold text-foreground mb-2 hover:text-primary transition-smooth">4</div>
              <p className="text-base font-body text-muted-foreground">
                International Certifications
              </p>
            </div>
            <div className="glass-card p-8 rounded-2xl border border-border text-center hover-lift transition-smooth">
              <div className="text-5xl font-display font-bold text-foreground mb-2 hover:text-primary transition-smooth">100%</div>
              <p className="text-base font-body text-muted-foreground">
                Compliance Rate
              </p>
            </div>
            <div className="glass-card p-8 rounded-2xl border border-border text-center hover-lift transition-smooth">
              <div className="text-5xl font-display font-bold text-foreground mb-2 hover:text-primary transition-smooth">500+</div>
              <p className="text-base font-body text-muted-foreground">
                Certified Shipments
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloatingButton />
    </main>);

};

export default AboutPage;